
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import "./../../styles/DocumentUpload.css";
import { CallApiForUploadDocument, CallApiForDeleteDocument, CallApiForGetCaseDocuments } from "../../helpers/http";
import WebcamModal from "../../Component/shared/webCam/Index";
import { CameraIcon, CheckIcon, DocIcon, UploadIcon } from "../../Component/shared/icons/Index";
import { CallApiForSetEhrImageId, deleteFileForEhr } from "./Service";
import { getHeaders } from "../../helpers/interceptor";


// ─── Helpers ────────────────────────────────────────────────────────────────

function buildDocsFromDocuments(documents = []) {
  const nameCounts = {};
  return documents.map((doc) => {
    const docId = doc.DocumentId ?? doc.documentId;
    const docName = doc.DocumentName ?? doc.documentName;
    const baseName = docName || "Document";
    nameCounts[baseName] = (nameCounts[baseName] || 0) + 1;
    const label =
      nameCounts[baseName] > 1 ? `${baseName} (${nameCounts[baseName]})` : baseName;
    return {
      id: String(docId),
      docId,
      label,
      subtitle: `Document: ${docName}`,
      iconBg: "#e6f4f3",
      Icon: DocIcon,
    };
  });
}

/**
 * EHR-style multipart upload — mirrors DocumentManager.formMultiFormObject()
 */
async function postMultipartUpload({
  nodeId,
  caseId,
  companyId,
  userId,
  userName,
  docId,
  docName,
  fileObjects,
  uploadUrl,
}) {
  const formData = new FormData();

  formData.append("NodeId", nodeId);
  formData.append("NodeType", "");
  formData.append("UserID", userId);
  formData.append("CompanyID", companyId);
  formData.append("Tocket", "");
  formData.append("NodeName", docName);
  formData.append("CaseID", caseId);
  formData.append("FileName", "");
  formData.append("UserName", userName);
  formData.append("EventId", "");

  for (const { fileObject, fileName } of fileObjects) {
    formData.append("files", fileObject, fileName);
  }

  return fetch(uploadUrl, {
    method: "POST",
    body: formData,
    headers: getHeaders(),
  });
}

/**
 * Converts a File to base64 string (for CallApiForUploadDocument)
 */
async function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Converts a webcam dataURL to a File object so it can be sent as multipart.
 */
function dataUrlToFile(dataUrl, fileName = "webcam-capture.jpg") {
  const [header, base64] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)[1];
  const binary = atob(base64);
  const arr = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
  return new File([arr], fileName, { type: mime });
}

/**
 * Converts an image File to a PDF File using canvas + jsPDF via dynamic import
 */
async function imageToPdfFile(imageFile, fileName) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(imageFile);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const A4_WIDTH = 794;
      const A4_HEIGHT = 1123;

      const imgRatio = img.width / img.height;
      const a4Ratio = A4_WIDTH / A4_HEIGHT;

      let drawWidth, drawHeight, offsetX, offsetY;
      if (imgRatio > a4Ratio) {
        drawWidth = A4_WIDTH;
        drawHeight = A4_WIDTH / imgRatio;
        offsetX = 0;
        offsetY = (A4_HEIGHT - drawHeight) / 2;
      } else {
        drawHeight = A4_HEIGHT;
        drawWidth = A4_HEIGHT * imgRatio;
        offsetX = (A4_WIDTH - drawWidth) / 2;
        offsetY = 0;
      }

      const canvas = document.createElement("canvas");
      canvas.width = A4_WIDTH;
      canvas.height = A4_HEIGHT;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, A4_WIDTH, A4_HEIGHT);
      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

      const jpegDataUrl = canvas.toDataURL("image/jpeg", 0.92);
      const base64Data = jpegDataUrl.split(",")[1];
      const jpegBytes = atob(base64Data);
      const jpegLength = jpegBytes.length;

      const contentStream = `q ${A4_WIDTH} 0 0 ${A4_HEIGHT} 0 0 cm /Im1 Do Q`;
      const contentLength = contentStream.length;

      const jpegArray = new Uint8Array(jpegLength);
      for (let i = 0; i < jpegLength; i++) {
        jpegArray[i] = jpegBytes.charCodeAt(i);
      }

      const encoder = new TextEncoder();

      const obj1 = `1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n`;
      const obj2 = `2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n`;
      const obj3 = `3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${A4_WIDTH} ${A4_HEIGHT}] /Contents 4 0 R /Resources << /XObject << /Im1 5 0 R >> >> >>\nendobj\n`;
      const obj4Header = `4 0 obj\n<< /Length ${contentLength} >>\nstream\n`;
      const obj4Footer = `\nendstream\nendobj\n`;
      const obj5Header = `5 0 obj\n<< /Type /XObject /Subtype /Image /Width ${A4_WIDTH} /Height ${A4_HEIGHT} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpegLength} >>\nstream\n`;
      const obj5Footer = `\nendstream\nendobj\n`;

      const header = `%PDF-1.4\n%âãÏÓ\n`;
      let offset = header.length;
      const offsets = [];

      offsets.push(offset);
      offset += obj1.length;

      offsets.push(offset);
      offset += obj2.length;

      offsets.push(offset);
      offset += obj3.length;

      offsets.push(offset);
      const obj4Full = obj4Header + contentStream + obj4Footer;
      offset += obj4Full.length;

      offsets.push(offset);
      const obj5FullHeader = obj5Header;
      offset += obj5FullHeader.length + jpegLength + obj5Footer.length;

      const xrefOffset = offset;
      let xref = `xref\n0 6\n0000000000 65535 f \n`;
      offsets.forEach((o) => {
        xref += String(o).padStart(10, "0") + " 00000 n \n";
      });

      const trailer = `trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

      const textParts = [header, obj1, obj2, obj3, obj4Full, obj5FullHeader];
      const textBytes = encoder.encode(textParts.join(""));
      const obj5FooterBytes = encoder.encode(obj5Footer);
      const xrefBytes = encoder.encode(xref + trailer);

      const totalLength = textBytes.length + jpegArray.length + obj5FooterBytes.length + xrefBytes.length;
      const pdfBytes = new Uint8Array(totalLength);

      let pos = 0;
      pdfBytes.set(textBytes, pos); pos += textBytes.length;
      pdfBytes.set(jpegArray, pos); pos += jpegArray.length;
      pdfBytes.set(obj5FooterBytes, pos); pos += obj5FooterBytes.length;
      pdfBytes.set(xrefBytes, pos);

      const pdfName = fileName.replace(/\.[^.]+$/, "") + ".pdf";
      const pdfBlob = new Blob([pdfBytes], { type: "application/pdf" });
      const pdfFile = new File([pdfBlob], pdfName, { type: "application/pdf" });
      resolve(pdfFile);
    };

    img.onerror = reject;
    img.src = objectUrl;
  });
}

const formatFileSize = (size) => {
  if (!size) return "";
  if (typeof size === "string") {
    if (size.includes("KB") || size.includes("MB") || size.includes("B")) {
      return size;
    }
  }
  const num = Number(size);
  if (isNaN(num) || num <= 0) return "";
  if (num >= 1024 * 1024) {
    return `${(num / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(num / 1024).toFixed(1)} KB`;
};

// ─── Component ──────────────────────────────────────────────────────────────

const DocumentUpload = forwardRef(function DocumentUpload(
  { nodes: documents, onApiSuccess, uploadedDocs, onUploadedDocsChange, onPendingChange },
  ref
) {
  // ── Session / token data ──────────────────────────────────────────────
  const tokenData = JSON.parse(sessionStorage.getItem("tokenData") || "{}");
  const caseId = tokenData?.caseId || tokenData?.CaseId || "";
  const companyId = tokenData?.companyId || tokenData?.CompanyId || "";
  const userId = tokenData?.userId || tokenData?.UserId || tokenData?.caseId || "patient";
  const userName = tokenData?.userName || tokenData?.UserName || userId;
  const nodeId = tokenData?.nodeId || tokenData?.NodeId || "";

  const uploadUrl = `${process.env.REACT_APP_DOCTOR_API_URL}DocumentManager/Upload`;
  // const uploadUrl = "https://gybapiuat.greenyourbills.com/api/DocumentManager/Upload";

  const resolvedDocuments = documents ?? JSON.parse(sessionStorage.getItem("tokenDocuments") || "[]");
  const DOCS = buildDocsFromDocuments(resolvedDocuments);
  const docsRef = useRef(DOCS);
  docsRef.current = DOCS;
  const onPendingChangeRef = useRef(onPendingChange);
  onPendingChangeRef.current = onPendingChange;
  const onUploadedDocsChangeRef = useRef(onUploadedDocsChange);
  onUploadedDocsChangeRef.current = onUploadedDocsChange;

  // ── State ─────────────────────────────────────────────────────────────
  const [uploaded, setUploaded] = useState(uploadedDocs || {});
  const [camModal, setCamModal] = useState({ open: false, docId: null });
  const [saving, setSaving] = useState(false);
  const [saveErrors, setSaveErrors] = useState({});
  const [deleting, setDeleting] = useState({});
  const [fileObjects, setFileObjects] = useState({});

  // ── Sync helper ────────────────────────────────────────────────────────
  const setUploadedAndSync = (updater) => {
    setUploaded((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      onUploadedDocsChangeRef.current?.(next);
      return next;
    });
  };

  // ── Pending change effect ──────────────────────────────────────────────
  const prevPendingRef = useRef(null);
  useEffect(() => {
    const currentDocs = docsRef.current || [];
    if (currentDocs.length === 0) return;
    const anyPending = currentDocs.some((doc) => !uploaded[doc.id]);
    if (prevPendingRef.current !== anyPending) {
      prevPendingRef.current = anyPending;
      onPendingChangeRef.current?.(anyPending);
    }
  }, [uploaded]);

  useEffect(() => {
    const loadUploadedDocs = async () => {
      if (!caseId) return;
      try {
        const res = await CallApiForGetCaseDocuments(caseId);
        if (!res?.success || !Array.isArray(res.data)) return;

        const currentDocs = docsRef.current || [];
        const validDocIds = new Set(currentDocs.map((d) => String(d.id)));
        const mapped = {};
        res.data.forEach((doc) => {
          const originalFileName = doc.fileName || doc.FileName || null;
          const fileNameFromPath = doc.storagePath?.split("/").pop() || "Uploaded file";

          // documentType holds the node ID that matches DOCS[n].id
          const key = String(doc.documentType ?? doc.documentId ?? doc.nodeId);
          const docSize =
            doc.fileSize ?? doc.FileSize ?? doc.size ?? doc.Size ?? doc.documentSize ?? doc.DocumentSize ?? 0;

          if (validDocIds.size === 0 || validDocIds.has(key)) {
            mapped[key] = {
              name: originalFileName || fileNameFromPath,
              url: doc.storagePath,
              uploaded: true,
              documentId: doc.id,
              ehrImageId: doc.ehrImageId ?? null,
              size: Number(docSize) || 0,
            };
          }
        });
        setUploadedAndSync(mapped);
      } catch (err) {
        console.error("Failed to load uploaded documents", err);
      }
    };

    loadUploadedDocs();
  }, [caseId]);

  // ── Expose submit via ref ──────────────────────────────────────────────
  useImperativeHandle(ref, () => ({ submit: handleSaveAll }));

  const uploadedCount = DOCS.filter((doc) => !!uploaded[doc.id]).length;
  const totalCount = DOCS.length;
  const progressPct = totalCount > 0 ? Math.min(100, Math.round((uploadedCount / totalCount) * 100)) : 0;

  // ── Commit a file into local fileObjects state ─────────────────────────
  const commitFile = async (docId, file, overrideFileName) => {
    if (!file) return;

    let finalFile = file;
    let fileName = overrideFileName || file.name;

    if (file.type.startsWith("image/")) {
      try {
        finalFile = await imageToPdfFile(file, fileName);
        fileName = finalFile.name;
      } catch (err) {
        console.warn("Image to PDF conversion failed, using original file:", err);
        finalFile = file;
      }
    }

    setFileObjects((prev) => ({
      ...prev,
      [docId]: { fileObject: finalFile, fileName, isEditFileName: false },
    }));

    setUploadedAndSync((prev) => ({
      ...prev,
      [docId]: {
        name: fileName,
        size: finalFile.size,
        url: URL.createObjectURL(finalFile),
        type: finalFile.type,
        file: finalFile,
      },
    }));

    setSaveErrors((prev) => { const n = { ...prev }; delete n[docId]; return n; });
  };

  // ── Rename file before upload ──────────────────────────────────────────
  const onChangeFileName = (docId, newName) => {
    const entry = fileObjects[docId];
    if (!entry) return;

    const extension = entry.fileObject.name.split(".").pop();
    let fileName = newName.includes("." + extension)
      ? newName
      : newName.replace(/\.[^.]+$/, "") + "." + extension;

    setFileObjects((prev) => ({
      ...prev,
      [docId]: { ...prev[docId], fileName },
    }));

    setUploadedAndSync((prev) => ({
      ...prev,
      [docId]: { ...prev[docId], name: fileName },
    }));
  };

  // ── Toggle edit mode for a file name ──────────────────────────────────
  const onClickEditFileName = (docId) => {
    setFileObjects((prev) => {
      const reset = Object.fromEntries(
        Object.entries(prev).map(([id, val]) => [id, { ...val, isEditFileName: false }])
      );
      return {
        ...reset,
        [docId]: { ...reset[docId], isEditFileName: !prev[docId]?.isEditFileName },
      };
    });
  };

  // ── File picker & camera ───────────────────────────────────────────────
  const triggerFilePicker = (docId) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,.pdf,application/pdf";
    input.onchange = (e) => commitFile(docId, e.target.files[0]);
    input.click();
  };

  const triggerCamera = (docId) => {
    setCamModal({ open: true, docId });
  };

  const handleWebcamCapture = (dataUrl) => {
    const { docId } = camModal;
    setCamModal({ open: false, docId: null });
    const file = dataUrlToFile(dataUrl, "webcam-capture.jpg");
    commitFile(docId, file);
  };

  // ── Remove / delete a document ─────────────────────────────────────────
  const removeDoc = async (docId) => {
    const fileInfo = uploaded[docId];
    setDeleting((prev) => ({ ...prev, [docId]: true }));

    try {
      // 1. Patient portal DB delete
      if (fileInfo?.uploaded && fileInfo?.documentId) {
        await CallApiForDeleteDocument(fileInfo.documentId);
      }

      // 2. EHR delete — only runs if ehrImageId is present
      if (fileInfo?.uploaded && fileInfo?.ehrImageId) {
        await deleteFileForEhr(
          fileInfo.ehrImageId,
          caseId,
          companyId,
          fileInfo.name ?? ""
        );
      }
    } catch (err) {
      console.warn("Delete failed:", err);
    }

    setDeleting((prev) => { const n = { ...prev }; delete n[docId]; return n; });
    setFileObjects((prev) => { const n = { ...prev }; delete n[docId]; return n; });
    setUploadedAndSync((prev) => { const n = { ...prev }; delete n[docId]; return n; });
    setSaveErrors((prev) => { const n = { ...prev }; delete n[docId]; return n; });
  };

  // ── Upload all staged files ────────────────────────────────────────────
  const handleSaveAll = async () => {
    setSaving(true);
    setSaveErrors({});
    const errors = {};

    const pendingEntries = Object.entries(fileObjects).filter(([docId, entry]) => {
      const info = uploaded[docId];
      return entry?.fileObject && !info?.uploaded;
    });

    await Promise.all(
      pendingEntries.map(async ([docId, entry]) => {
        const docMeta = DOCS.find((d) => d.id === docId);

        // ── Prepare base64 for patient portal API ─────────────────────────
        let fileContent = null;
        try {
          fileContent = await toBase64(entry.fileObject);
        } catch (err) {
          console.warn("toBase64 failed (CallApiForUploadDocument will be skipped):", err);
        }

        // ── Fire both APIs in parallel ────────────────────────────────────
        //  KEY FIX: capture patientUploadResult from Promise.all index [1]
        const [ehrResponse, patientUploadResult] = await Promise.all([
          // 1. EHR multipart — result drives success/failure
          postMultipartUpload({
            nodeId,
            caseId,
            companyId,
            userId,
            userName,
            docId: docMeta?.docId,
            docName: docMeta?.label || entry.fileName,
            fileObjects: [{ fileObject: entry.fileObject, fileName: entry.fileName }],
            uploadUrl,
          }),

          // 2. Patient portal DB — now captured, returns { success, data: { id, ... } }
          fileContent
            ? CallApiForUploadDocument({
              NodeId: nodeId,
              SZ_Case_Id: caseId,
              SZ_Company_Id: companyId,
              UploadUser_Id: userId,
              DocumentId: docMeta?.docId,
              DocumentName: docMeta?.label,
              FileContent: fileContent,
              FileName: entry.fileName,
              MimeType: entry.fileObject.type || "application/octet-stream",
              FileSize: entry.fileObject.size || 0,
            }).catch((err) => {
              console.warn(`CallApiForUploadDocument failed (ignored):`, err);
              return null; 
            })
            : Promise.resolve(null),
        ]);

        // ── Handle EHR response ───────────────────────────────────────────
        if (ehrResponse.status === 401) {
          errors[docId] = "Session expired. Please log in again.";
          return;
        }

        let ehrResult;
        try {
          ehrResult = await ehrResponse.json();
        } catch {
          ehrResult = null;
        }

        if (!ehrResponse.ok || ehrResult?.HasException === true) {
          errors[docId] = ehrResult?.Exception || ehrResult?.message || "Upload failed";
        } else {
          // ehrImageId comes from EHR response — "Data": ["28737764"]
          const ehrImageId =
            typeof ehrResult?.Data?.[0] === "string"
              ? ehrResult.Data[0]
              : ehrResult?.Data?.[0]?.Id ?? null;

          //  patientDocumentId comes from patient portal response — "data": { "id": 121 }
          const patientDocumentId = patientUploadResult?.data?.id ?? null;

          //  Patch ehrImageId into patient DB now that we have both IDs
          if (ehrImageId && patientDocumentId) {
            CallApiForSetEhrImageId(patientDocumentId, String(ehrImageId)).catch((err) =>
              console.warn("Failed to persist ehrImageId:", err)
            );
          }

          setUploadedAndSync((prev) => ({
            ...prev,
            [docId]: {
              ...prev[docId],
              file: null,
              uploaded: true,
              documentId: patientDocumentId,  
              ehrImageId: ehrImageId,          
            },
          }));

          setFileObjects((prev) => { const n = { ...prev }; delete n[docId]; return n; });
        }
      })
    );

    setSaving(false);

    if (Object.keys(errors).length > 0) {
      setSaveErrors(errors);
    } else {
      onApiSuccess?.();
    }
  };

  // ── Early return AFTER all hooks ──────────────────────────────────────
  if (DOCS.length === 0) {
    return (
      <div className="doc-upload-root">
        <div className="du-empty" style={{ margin: 0 }}>
          <p className="du-empty-title">No documents available.</p>
          <p className="du-empty-hint">No document nodes were found in your session.</p>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="doc-upload-root">

      {camModal.open && (
        <WebcamModal
          docLabel={DOCS.find((d) => d.id === camModal.docId)?.label || ""}
          onCapture={handleWebcamCapture}
          onClose={() => setCamModal({ open: false, docId: null })}
          onUploadClick={() => {
            const currentDocId = camModal.docId;
            setCamModal({ open: false, docId: null });
            if (currentDocId) triggerFilePicker(currentDocId);
          }}
        />
      )}

      {/* Progress bar */}
      <div className="du-progress-row">
        <div className="du-progress-track">
          <div className="du-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <span className="du-progress-label">
          {uploadedCount} of {totalCount} uploaded
        </span>
      </div>

      {/* Single panel */}
      <div className="du-single-panel">
        <div className="du-panel-head">
          <span className="du-panel-title">Documents</span>
          <span className="du-panel-sub">Camera or Upload</span>
        </div>

        <div className="du-doc-list">
          {DOCS.map((doc) => {
            const fileInfo = uploaded[doc.id];
            const stagedEntry = fileObjects[doc.id];
            const isUploaded = fileInfo?.uploaded || !!fileInfo;
            const hasError = !!saveErrors[doc.id];
            const isImg = fileInfo?.type?.startsWith("image/") || fileInfo?.isDataUrl;
            const isEditing = stagedEntry?.isEditFileName;

            let itemClass = "du-doc-item";
            if (isUploaded && !hasError)
              itemClass += fileInfo?.uploaded ? " du-doc-item--uploaded" : " du-doc-item--done";

            return (
              <div key={doc.id} className={itemClass}>
                <div className="du-doc-main">

                  {isUploaded && isImg ? (
                    <img src={fileInfo.url} alt={doc.label} className="du-file-thumb-inline" />
                  ) : (
                    <div
                      className="du-doc-icon"
                      style={{ background: hasError ? "#fee2e2" : doc.iconBg }}
                    >
                      <doc.Icon />
                    </div>
                  )}

                  <div className="du-doc-text">
                    <div className="du-doc-label">{doc.label}</div>

                    {(hasError || (isUploaded && fileInfo)) && (
                      <div className="du-doc-subtext">
                        {hasError ? (
                          <span style={{ color: "#dc2626" }}>⚠ {saveErrors[doc.id]}</span>
                        ) : isEditing ? (
                          <input
                            className="du-rename-input"
                            type="text"
                            value={stagedEntry.fileName}
                            onChange={(e) => onChangeFileName(doc.id, e.target.value)}
                            onBlur={() => onClickEditFileName(doc.id)}
                            autoFocus
                          />
                        ) : (
                          <span className="du-file-inline-meta">
                            <span className="du-file-inline-name">{fileInfo.name}</span>
                            {formatFileSize(fileInfo.size) && (
                              <span className="du-file-inline-size">
                                {` · ${formatFileSize(fileInfo.size)}`}
                              </span>
                            )}
                            {!fileInfo.uploaded && stagedEntry && (
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                title="Rename file"
                                style={{ cursor: "pointer", marginLeft: 6, flexShrink: 0 }}
                                onClick={() => onClickEditFileName(doc.id)}
                              >
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            )}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="du-doc-actions">
                    {!isUploaded ? (
                      <>
                        <button
                          type="button"
                          title="Use camera"
                          className="du-icon-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            triggerCamera(doc.id);
                          }}
                        >
                          <CameraIcon />
                        </button>
                        <button
                          type="button"
                          title="Upload file"
                          className="du-icon-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            triggerFilePicker(doc.id);
                          }}
                        >
                          <UploadIcon />
                        </button>
                        <span className="du-badge du-badge--pending">Pending</span>
                      </>
                    ) : (
                      <>
                        {fileInfo?.uploaded ? (
                          <span className="du-badge du-badge--done">
                            <CheckIcon /> Done
                          </span>
                        ) : (
                          <span className="du-badge du-badge--ready">
                            <CheckIcon /> Ready
                          </span>
                        )}
                        <button
                          className="du-remove-btn"
                          title="Remove"
                          disabled={!!deleting[doc.id]}
                          onClick={(e) => { e.stopPropagation(); removeDoc(doc.id); }}
                        >
                          {deleting[doc.id] ? <span className="du-remove-spinner" /> : "✕"}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Upload error summary */}
      {Object.keys(saveErrors).length > 0 && (
        <div className="du-error-summary">
          ⚠ Some documents failed to upload. Please check the highlighted items above and try again.
        </div>
      )}

      {/* Saving indicator */}
      {saving && (
        <div className="du-saving-indicator">
          <div className="du-saving-spinner" />
          Uploading documents…
        </div>
      )}
    </div>
  );
});

export default DocumentUpload;